import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators, FormBuilder, FormArray, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, map, startWith, switchMap, tap } from 'rxjs/operators';

// Models
import { MetaData } from 'src/app/platform/models/utils';
import { AddressData, User } from 'src/app/modules/account/models/user';

// Services
import { UiService } from 'src/app/platform/services/ui.service';
import { UserService } from 'src/app/modules/account/user.service';
import { AccountsService } from 'src/app/modules/account/accounts.service';
import { GeodataService } from 'src/app/platform/services/geodata.service';
import { PlatformService } from 'src/app/platform/services/platform.service';

// Components
import { AccountPasswordChangeComponent } from 'src/app/modules/account/views/account-password-change/account-password-change.component';
import { ProceduresNewComponent } from 'src/app/modules/procedures/views/procedures-new/procedures-new.component';

@Component({
  selector: 'app-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss']
})
export class AccountComponent implements OnInit {
  public user: User = new User();
  public stateData: Array<MetaData> = [];
  public streetLoading = false;

  public departmentBillingData: Array<MetaData> = [];
  public cityBillingData: Array<MetaData> = [];
  public streetBillingData: Array<MetaData> = [];
  public streetBillingFilteredData: Observable<Array<MetaData>>;

  public departmentConectionData: Array<MetaData> = [];
  public cityConectionData: Array<MetaData> = [];
  public streetConectionData: Array<MetaData> = [];
  public streetConectionFilteredData: Observable<Array<MetaData>>;

  public formData: FormGroup;

  constructor(
    private dialog: MatDialog,
    private uiDialog: MatDialog,
    private uiService: UiService,
    private formBuilder: FormBuilder,
    private userService: UserService,
    private geodataService: GeodataService,
    private accountsService: AccountsService,
    private platformService: PlatformService,
  ) {
    this.userService.currentUser.subscribe((user) => {
      this.user = user;
    });

    this.streetBillingData.push(new MetaData(this.user.addressBilling.street.id, this.user.addressBilling.street.name));
    this.streetConectionData.push(new MetaData(this.user.addressConection.street.id, this.user.addressConection.street.name));

    this.formData = this.formBuilder.group({
      firstname: new FormControl(this.user.firstname, [Validators.required]),
      lastname: new FormControl(this.user.lastname, []),
      dni: new FormControl(this.user.dni, [Validators.required]),
      email: new FormControl(this.user.email, [Validators.required]),
      emails: this.formBuilder.array([]),
      phoneNumber: new FormControl(((this.user.phones.length > 0) ? this.user.phones[0].number : 0), [Validators.required, this.validatePhone()]),
      phoneType: new FormControl(((this.user.phones.length > 0) ? this.user.phones[0].type : 'Celular'), [Validators.required]),
      phones: this.formBuilder.array([]),
      addressSame: new FormControl(false, []),
      addressConectionState: new FormControl(this.user.addressConection.city.state.id, [Validators.required]),
      addressConectionDepartment: new FormControl(this.user.addressConection.city.department.id, [Validators.required]),
      addressConectionCity: new FormControl(this.user.addressConection.city.id, [Validators.required]),
      addressConectionStreet: new FormControl(this.user.addressConection.street.id, [Validators.required]),
      addressConectionNumber: new FormControl(this.user.addressConection.number, [Validators.required]),
      addressConectionApartment: new FormControl(this.user.addressConection.apartment, []),
      addressBillingState: new FormControl(this.user.addressBilling.city.state.id, [Validators.required]),
      addressBillingDepartment: new FormControl(this.user.addressBilling.city.department.id, [Validators.required]),
      addressBillingCity: new FormControl(this.user.addressBilling.city.id, [Validators.required]),
      addressBillingStreet: new FormControl(this.user.addressBilling.street.id, [Validators.required]),
      addressBillingNumber: new FormControl(this.user.addressBilling.number, [Validators.required]),
      addressBillingApartment: new FormControl(this.user.addressBilling.apartment, []),
    });

    this.user.emails.forEach(email => {
      const emailsControl = <FormArray> this.formData.controls['emails'];

      emailsControl.push(this.formBuilder.group({email: [email, [Validators.required]]}));
    });

    this.user.phones.forEach(phone => {
      const phonesControl = <FormArray> this.formData.controls['phones'];

      phonesControl.push(this.formBuilder.group({
        phoneNumber: [phone.number, [Validators.required, this.validatePhone()]],
        phoneType: [phone.type, [Validators.required]]
      }));
    });

    this.uiService.showSpinner(true);

    this.geodataService.statesGet().then((success) => {
      this.stateData = success.result;

      const promises: Array<any> = [];
      const promisesType: Array<any> = [];

      if (this.user.addressBilling.city.state.id > 0) {
        promises.push(this.geodataService.departmentsGet(this.user.addressBilling.city.state.id));
        promisesType.push('addressBillingDepartment');

        if (this.user.addressBilling.city.department.id > 0) {
          promises.push(this.geodataService.citiesGet(this.user.addressBilling.city.state.id, this.user.addressBilling.city.department.id));
          promisesType.push('addressBillingCity');
        }
      }

      if (this.user.addressConectionUpdated) {
        if (this.user.addressConection.city.state.id > 0) {
          promises.push(this.geodataService.departmentsGet(this.user.addressConection.city.state.id));
          promisesType.push('addressConectionDepartment');

          if (this.user.addressConection.city.department.id > 0) {
            promises.push(this.geodataService.citiesGet(this.user.addressConection.city.state.id, this.user.addressConection.city.department.id));
            promisesType.push('addressConectionCity');
          }
        }
      }

      if (promises.length > 0) {
          Promise.all(promises).then(values => {
            this.uiService.showSpinner(false);

            promisesType.forEach((element, index) => {
              if (element === 'addressBillingDepartment') {
                this.departmentBillingData = values[index].result;
              } else if (element === 'addressBillingCity') {
                this.cityBillingData = values[index].result;
              } else if (element === 'addressConectionDepartment') {
                this.departmentConectionData = values[index].result;
              } else if (element === 'addressConectionCity') {
                this.cityConectionData = values[index].result;
              }
            });

            // The values are set at this time after the loading of streets has finished, otherwise there is no list to show the data.
            this.formData.patchValue({addressConectionStreet: this.user.addressConection.street.id});
            this.formData.patchValue({addressBillingStreet: this.user.addressBilling.street.id});
          }).catch(() => {
            this.uiService.showSpinner(false);
            this.uiService.dialogShow('Ups!', 'No es posible actualizar los datos de tu cuenta en este momento. Estamos trabajando para solucionar el inconveniente.');
          });
      } else{
        this.uiService.showSpinner(false);
      }
    }).catch((error) => {
      this.uiService.showSpinner(false);
      this.uiService.dialogShow('Ups!', 'No es posible actualizar los datos de tu cuenta en este momento. Estamos trabajando para solucionar el inconveniente.');
    });

    this.streetBillingFilteredData = this.formData.get('addressBillingStreet')!.valueChanges.pipe(
      startWith(''), debounceTime(300), distinctUntilChanged(), switchMap(value => this.streetDataFilter('billing', this.formData.get('addressBillingState')?.value, value))
    );

    if (this.user.addressConectionUpdated) {
      this.streetConectionFilteredData = this.formData.get('addressConectionStreet')!.valueChanges.pipe(
        startWith(''), debounceTime(300), distinctUntilChanged(), switchMap(value => this.streetDataFilter('conection', this.formData.get('addressConectionState')?.value, value))
      );
    }
  }

  ngOnInit(): void { }

  onUserdataForm(): void {
    this.uiDialog.open(AccountPasswordChangeComponent, {
      width: '400px',
    });
  }

  onSave(): void {
    if (this.formData.valid) {
      let addressBilling: AddressData = new AddressData();
      const addressConection: AddressData = new AddressData();

      addressBilling.city.state.id = this.formData.get('addressBillingState')?.value;
      addressBilling.city.department.id = this.formData.get('addressBillingDepartment')?.value;
      addressBilling.city.id = this.formData.get('addressBillingCity')?.value;
      addressBilling.street.id = this.formData.get('addressBillingStreet')?.value;
      addressBilling.number = this.formData.get('addressBillingNumber')?.value;
      addressBilling.apartment = this.formData.get('addressBillingApartment')?.value;

      addressConection.city.state.id = this.formData.get('addressConectionState')?.value;
      addressConection.city.department.id = this.formData.get('addressConectionDepartment')?.value;
      addressConection.city.id = this.formData.get('addressConectionCity')?.value;
      addressConection.street.id = this.formData.get('addressConectionStreet')?.value;
      addressConection.number = this.formData.get('addressConectionNumber')?.value;
      addressConection.apartment = this.formData.get('addressConectionApartment')?.value;

      if (this.formData.get('addressSame')?.value) {
        addressBilling = addressConection;
      }

      const emails: Array<string> = [];

      this.formData.get('emails')?.value.forEach((email: any) => {
        emails.push(email.email);
      });

      const phones: Array<any> = [];

      this.formData.get('phones')?.value.forEach((phone: any) => {
        phones.push({ani: phone.phoneNumber, tipo: phone.phoneType});
      });

      this.uiService.showSpinner(true);

      this.accountsService.accountUpdate(
        this.user.firstname, // this.formData.get('firstname')?.value,
        this.user.lastname, // this.formData.get('lastname')?.value,
        this.user.dni, // this.formData.get('dni')?.value,
        emails,
        phones,
        addressConection,
        addressBilling,
      ).then((result) => {
        if (result.status) {
          this.uiService.showSpinner(false);
          this.uiService.dialogShow('Datos modificados', 'Tus datos fueron actualizados correctamente.', 'success');

          this.userService.update(this.accountsService.accountDataStructure(result.result));
        }
        else {
          this.uiService.showSpinner(false);
          this.uiService.dialogShow('Ups!', 'Hubo un inconveniente al intentar actualizar los datos. Por favor intenta nuevamente.');
        }
      }, (error) => {
        this.uiService.showSpinner(false);
        this.uiService.dialogShow('Ups!', 'No es posible actualizar los datos en este momento. Estamos trabajando para solucionar el inconveniente.');
      });
    } else {
      this.formData.markAllAsTouched();
      this.uiService.showSnackBar('Algunos datos no están completos');
    }
  }

  // Emails functions

  emailsFields(): Array<any> {
    return (this.formData.get('emails') as FormArray).controls;
  }

  onEmailAdd(): void {
    const emailsControl = <FormArray> this.formData.controls['emails'];

    emailsControl.push(this.formBuilder.group({email: ['', [Validators.required]]}));
  }

  onEmailDelete(index: number): void {
      const emailsControl = <FormArray> this.formData.controls['emails'];

      emailsControl.removeAt(index);
  }

  // Phones functions

  phonesFields(): Array<any> {
    return (this.formData.get('phones') as FormArray).controls;
  }

  onPhoneAdd(): void {
    const phonesControl = <FormArray> this.formData.controls['phones'];

    phonesControl.push(this.formBuilder.group({
      phoneNumber: ['', [Validators.required, this.validatePhone()]],
      phoneType: ['', [Validators.required]]
    }));
  }

  onPhoneDelete(index: number): void {
      const phonesControl = <FormArray> this.formData.controls['phones'];

      phonesControl.removeAt(index);
  }

  // Address billing functions

  streetBillingNameGet(id: number): string {
    return this.streetNameGet(this.streetBillingData, id);
  }

  // Address conection functions

  streetConectionNameGet(id: number): string {
    return this.streetNameGet(this.streetConectionData, id);
  }

  // Common conection functions

  onDataChange(address: string, type: string): void {
    if (type === 'state') {
      if (address === 'billing') {
        this.getDepartmentData(this.formData.get('addressBillingState')?.value).then((response) => {
          this.departmentBillingData = response;
        });
      } else if (address === 'conection') {
        this.getDepartmentData(this.formData.get('addressConectionState')?.value).then((response) => {
          this.departmentConectionData = response;
        });
      }
    } else if (type === 'department') {
      if (address === 'billing') {
        this.getCityData(this.formData.get('addressBillingState')?.value, this.formData.get('addressBillingDepartment')?.value).then((response) => {
          this.cityBillingData = response;
        });
      } else if (address === 'conection') {
        this.getCityData(this.formData.get('addressConectionState')?.value, this.formData.get('addressConectionDepartment')?.value).then((response) => {
          this.cityConectionData = response;
        });
      }
    }
  }

  streetNameGet(data: Array<MetaData>, id: number): string {
    let response = '';

    if (data.length > 0) {
      if (data[0] instanceof MetaData) {
        response = data.find(street => street.id === id)!.name;
      }
    }

    return response;
  }

  private streetDataFilter(type: string, state: number, value: string): Promise<Array<MetaData>> {
    return new Promise((resolve, reject) => {
      // If the value of the field is not empty and if it is not a whole
      // number since when selecting a value the field is updated by the ID
      if (value !== '' && isNaN(+value)) {
        this.streetLoading = true;

        this.getStreetData(value, state).then((response) => {
          this.streetLoading = false;

          if (type === 'conection') {
            this.streetConectionData = response;
          } else if (type === 'billing') {
            this.streetBillingData = response;
          }

          resolve(response.filter(option => option.name.toLowerCase().includes(String(value).toLowerCase())));
        });
      } else {
        let data: Array<MetaData> = [];

        if (type === 'conection') {
          data = this.streetConectionData;
        } else if (type === 'billing') {
          data = this.streetBillingData;
        }

        resolve(data);
      }
    });
  }

  addressSame(): boolean {
    return (this.formData.get('addressSame')?.value !== null) ? this.formData.get('addressSame')?.value : false;
  }

  formHasError(formcontrol: string, data: string): any {
    let fields: any = {
      firstname: {
        required:  'Ingresá un nombre'
      },
      lastname: {
        required:  'Ingresá un apellido',
      },
      dni: {
        required:  'Ingresá un DNI',
      },
      email: {
        required:  'Ingresá un email',
      },
      phoneNumber: {
        required:  'Ingresá un número de teléfono',
        invalid:  'El número debe tener 10 dígitos sin anteponer 0 ni 15'
      }
    };

    if (!this.addressSame()) {
      fields.addressBillingState = {
        required: 'Seleccioná una provincia',
      };
      fields.addressBillingDepartment = {
        required: 'Seleccioná un departamento',
      };
      fields.addressBillingCity = {
        required: 'Seleccioná una localidad',
      };
      fields.addressBillingStreet = {
        required: 'Seleccioná una calle',
      };
      fields.addressBillingNumber = {
        required: 'Ingresá un número',
      };
    }

    if (this.user.addressConectionUpdated) {
      fields.addressConectionState = {
        required: 'Seleccioná una provincia',
      };
      fields.addressConectionDepartment = {
        required: 'Seleccioná un departamento',
      };
      fields.addressConectionCity = {
        required: 'Seleccioná una localidad',
      };
      fields.addressConectionStreet = {
        required: 'Seleccioná una calle',
      };
      fields.addressConectionNumber = {
        required: 'Ingresá un número',
      };
    }

    return this.platformService.formHasError(this.formData, fields, formcontrol, data);
  }

  private validatePhone(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isValid = this.platformService.validarTelefono(String(control.value));
      let validationResult: ValidationErrors | null = (isValid === true) ? null : { invalid: true };

      if (typeof validationResult === 'undefined' || control.value == '') {
        validationResult = null;
      }

      return validationResult;
    };
  }

  getStateData(): Promise<Array<MetaData>> {
    return new Promise((resolve, reject) => {
      this.geodataService.statesGet().then((success) => {
        resolve(success.result);
      }).catch((error) => {
        resolve([new MetaData().configData({id: 0, nombre: 'Listado de provincias no disponible'})]);
      });
    });
  }

  getDepartmentData(state: number): Promise<Array<MetaData>> {
    return new Promise((resolve, reject) => {
      this.geodataService.departmentsGet(state).then((success) => {
        resolve(success.result);
      }).catch((error) => {
        resolve([new MetaData().configData({id: 0, nombre: 'Listado de departamentos no disponible'})]);
      });
    });
  }

  getCityData(state: number, department: number): Promise<Array<MetaData>> {
    return new Promise((resolve, reject) => {
      this.geodataService.citiesGet(state, department).then((success) => {
        resolve(success.result);
      }).catch((error) => {
        resolve([new MetaData().configData({id: 0, nombre: 'Listado de localidades no disponible'})]);
      });
    });
  }

  getStreetData(query: string, state: number): Promise<Array<MetaData>> {
    return new Promise((resolve, reject) => {
      this.geodataService.streetsGet(query, state).then((success) => {
        resolve(success.result);
      }).catch((error) => {
        resolve([new MetaData().configData({id: 0, nombre: 'Listado de calles no disponible'})]);
      });
    });
  }

  onNewProcedure(): void {
    this.dialog.open(ProceduresNewComponent, {
      width: '400px'
    });
  }

  help(title: string, message: string): void {
    this.uiService.dialogShow(title, message);
  }
}
